// MUI theme built from design tokens. Preserves every component override
// from the prior inline theme so existing components keep the same API,
// but swaps values to the new RestockIQ-inspired indigo palette.

import { createTheme } from '@mui/material/styles';
import { colors as c, typography as t, radii, shadows, transitions } from './tokens';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: c.primary.main,
      dark: c.primary.dark,
      light: c.primary.light,
      contrastText: c.primary.contrastText,
    },
    secondary: {
      main: c.secondary.main,
      dark: c.secondary.dark,
      light: c.secondary.light,
      contrastText: c.secondary.contrastText,
    },
    background: {
      default: c.bg.base,
      paper:   c.bg.paper,
    },
    error:   { main: c.danger.main },
    warning: { main: c.warning.main },
    info:    { main: c.info.main },
    success: { main: c.success.main },
    text: {
      primary:   c.text.primary,
      secondary: c.text.secondary,
      disabled:  c.text.disabled,
    },
    divider: c.border.divider,
  },

  typography: {
    fontFamily: t.fontFamily.base,
    h1: {
      fontFamily:    t.fontFamily.display,
      fontWeight:    t.weight.extrabold,
      fontSize:      t.size['4xl'],
      letterSpacing: t.letterSpacing.tightest,
      lineHeight:    t.lineHeight.tight,
    },
    h2: {
      fontFamily:    t.fontFamily.display,
      fontWeight:    t.weight.bold,
      fontSize:      t.size['3xl'],
      letterSpacing: t.letterSpacing.tight,
      lineHeight:    t.lineHeight.tight,
    },
    h3: {
      fontFamily:    t.fontFamily.display,
      fontWeight:    t.weight.bold,
      fontSize:      t.size['2xl'],
      letterSpacing: '-0.015em',
      lineHeight:    t.lineHeight.tight,
    },
    h4: {
      fontFamily:    t.fontFamily.display,
      fontWeight:    t.weight.bold,
      fontSize:      t.size.xl,
      letterSpacing: t.letterSpacing.normal,
    },
    h5: {
      fontFamily: t.fontFamily.display,
      fontWeight: t.weight.semibold,
      fontSize:   t.size.md,
    },
    h6: {
      fontFamily: t.fontFamily.display,
      fontWeight: t.weight.semibold,
      fontSize:   t.size.base,
    },
    subtitle1: {
      fontWeight: t.weight.medium,
      fontSize:   t.size.md,
      color:      c.text.secondary,
    },
    subtitle2: {
      fontWeight: t.weight.medium,
      fontSize:   t.size.base,
      color:      c.text.secondary,
    },
    button: {
      fontFamily:    t.fontFamily.display,
      fontWeight:    t.weight.semibold,
      textTransform: 'none',
      letterSpacing: t.letterSpacing.wide,
    },
  },

  shape: {
    borderRadius: radii.lg, // 12
  },

  components: {
    // --- Buttons ---
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md, // 8
          padding: '9px 20px',
          boxShadow: 'none',
          transition: `all ${transitions.base}`,
          '&:hover': {
            boxShadow: `0 4px 12px rgba(99, 102, 241, 0.22)`,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${c.primary[500]} 0%, ${c.primary[400]} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${c.primary[600]} 0%, ${c.primary[500]} 100%)`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${c.secondary[600]} 0%, ${c.secondary[500]} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${c.secondary[700]} 0%, ${c.secondary[600]} 100%)`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            boxShadow: '0 2px 8px rgba(15, 15, 18, 0.08)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: c.bg.hover,
            boxShadow: 'none',
            transform: 'none',
          },
        },
      },
    },

    // --- Cards ---
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: shadows.sm,
          borderRadius: radii.xl, // 16
          border: `1px solid ${c.border.subtle}`,
          transition: `all ${transitions.slow}`,
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
      },
    },

    // --- AppBar ---
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadows.xs,
          backdropFilter: 'blur(8px)',
        },
      },
    },

    // --- Dialogs ---
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii['2xl'], // 20
          boxShadow: shadows.dialog,
          border: `1px solid ${c.border.subtle}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: t.fontFamily.display,
          fontSize: t.size.xl,
          fontWeight: t.weight.bold,
          padding: '24px 28px 16px',
          letterSpacing: t.letterSpacing.normal,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '12px 28px 28px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '16px 28px 24px', gap: 12 },
      },
    },

    // --- Backdrop ---
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: c.bg.overlay,
          backdropFilter: 'blur(6px)',
        },
      },
    },

    // --- Text Fields ---
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radii.md,
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: c.primary.main,
            },
            '&.Mui-focused': {
              boxShadow: shadows.focusRing,
            },
          },
          '& .MuiInputLabel-root': {
            fontWeight: t.weight.medium,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          transition: 'box-shadow 0.2s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: c.primary.main,
          },
          '&.Mui-focused': {
            boxShadow: shadows.focusRing,
          },
        },
        notchedOutline: {
          borderColor: c.border.default,
          transition: 'border-color 0.2s ease',
        },
      },
    },

    // --- Select ---
    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: radii.md },
      },
    },

    // --- Stepper ---
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: '2rem',
          transition: 'all 0.3s ease',
          '&.Mui-completed': { color: c.success.main },
          '&.Mui-active': {
            color: c.primary.main,
            filter: `drop-shadow(0 2px 4px rgba(99, 102, 241, 0.3))`,
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontFamily: t.fontFamily.display,
          fontWeight: t.weight.medium,
          fontSize: t.size.base,
          transition: 'all 0.2s ease',
          '&.Mui-active': {
            fontWeight: t.weight.bold,
            color: c.text.primary,
          },
          '&.Mui-completed': {
            fontWeight: t.weight.semibold,
            color: c.success.main,
          },
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: c.border.default,
          borderWidth: 2,
        },
      },
    },

    // --- Tables ---
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: t.fontFamily.display,
            fontWeight: t.weight.bold,
            fontSize: t.size.xs,
            letterSpacing: t.letterSpacing.wider,
            textTransform: 'uppercase',
            color: c.text.secondary,
            backgroundColor: c.table.headerBg,
            borderBottom: `2px solid ${c.table.headerBorder}`,
            padding: '14px 16px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: c.table.rowHover },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: c.table.cellBorder,
          padding: '12px 16px',
        },
      },
    },

    // --- Chips ---
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: t.weight.medium,
          borderRadius: radii.md,
          transition: 'all 0.15s ease',
        },
        colorSuccess: { backgroundColor: c.success.bg, color: c.success.hover },
        colorError:   { backgroundColor: c.danger.bg,  color: c.danger.hover },
        colorWarning: { backgroundColor: c.warning.bg, color: c.warning.hover },
        colorInfo:    { backgroundColor: c.info.bg,    color: c.info.hover },
      },
    },

    // --- Alerts ---
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          fontWeight: t.weight.medium,
          alignItems: 'center',
        },
        standardSuccess: { backgroundColor: c.success.bg, border: `1px solid ${c.success.border}` },
        standardError:   { backgroundColor: c.danger.bg,  border: `1px solid ${c.danger.border}` },
        standardWarning: { backgroundColor: c.warning.bg, border: `1px solid ${c.warning.border}` },
        standardInfo:    { backgroundColor: c.info.bg,    border: `1px solid ${c.info.border}` },
      },
    },

    // --- Paper ---
    MuiPaper: {
      styleOverrides: {
        root: { transition: 'box-shadow 0.2s ease' },
        elevation1: { boxShadow: shadows.sm },
        elevation3: { boxShadow: shadows.md },
      },
    },

    // --- Tooltips ---
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: radii.md,
          fontSize: t.size.sm,
          fontWeight: t.weight.medium,
          padding: '8px 14px',
          backgroundColor: c.tooltip.bg,
          boxShadow: '0 4px 12px rgba(15, 15, 18, 0.15)',
        },
        arrow: { color: c.tooltip.bg },
      },
    },

    // --- Tabs ---
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: t.fontFamily.display,
          fontWeight: t.weight.semibold,
          textTransform: 'none',
          fontSize: '0.9rem',
          minHeight: 48,
          transition: 'color 0.2s ease',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },

    // --- Drawer ---
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${c.border.divider}`,
          boxShadow: 'none',
        },
      },
    },

    // --- Accordion ---
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          border: `1px solid ${c.border.subtle}`,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '8px 0' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontWeight: t.weight.semibold,
          minHeight: 52,
          '&.Mui-expanded': { minHeight: 52 },
        },
      },
    },

    // --- List Items ---
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          margin: '2px 8px',
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: c.bg.hover },
          '&.Mui-selected': {
            backgroundColor: c.bg.selected,
            '&:hover': { backgroundColor: c.bg.pressed },
          },
        },
      },
    },

    // --- Breadcrumbs ---
    MuiBreadcrumbs: {
      styleOverrides: { root: { fontSize: t.size.base } },
    },

    // --- Divider ---
    MuiDivider: {
      styleOverrides: { root: { borderColor: c.border.divider } },
    },

    // --- Switch ---
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        track: { borderRadius: 12, opacity: 0.3 },
      },
    },
  },
});

export default theme;
